import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { DepartmentType } from '../../../../gql/gql-types';
import { BreadCrumb } from '../../../components/BreadCrumb';
import { Card } from '../../../components/Card';
import { Modal } from '../../../components/Modal';
import { Routes } from '../../../constants/Routes';
import { URLSearchParamsMonad } from '../../../domains/base/SearchParams';
import { useContext } from '../../../hooks/useContext';
import { useSteps } from '../../../hooks/useSteps';
import { SessionStore } from '../../../stores/SessionStore';
import { AccountRestrictedToast } from '../components/AccountRestrictedToast';
import { VacanciesStore } from '../stores/VacanciesStore';
import { CourseSelectPage } from './CourseSelect';
import { DepartmentSelectPage } from './DepartmentSelect';
import { useAutoDepartmentAndCourseSelect } from './hooks/useAutoDepartmentAndCourseSelect';
import { useTreatmentRegisterFlow } from './hooks/useTreatmentRegisterFlow';
import { useWaitingListRegisterFlow } from './hooks/useWaitingListRegisterFlow';
import { RevisitSelectPage } from './RevisitSelect';
import { CourseStore } from './stores/CourseStore';
import { ReservationStore } from './stores/ReservationStore';
import { WaitingListStore } from './stores/WaitingListStore';
import { VacancySelectPage } from './VacancySelect';

type Step = 'DepartmentSelect' | 'VacancySelect' | 'CourseSelect' | 'RevisitSelect';
/**
 * 予約登録画面
 */
export const ReservationNewPage = observer(() => {
  const sessionStore = useContext(SessionStore.Context);
  const location = useLocation();
  const { push } = useHistory();

  // URLクエリ取得
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const departmentId = query.get('department');
  const treatmentKindId = query.get('treatmentKind');

  // 初期ステップはクエリ（department／treatmentKind）の有無で決定
  const initialStep: Step = useMemo(() => {
    if (departmentId && treatmentKindId) {
      // department と treatmentKind 両方ある → 日付選択（VacancySelect）
      return 'VacancySelect';
    } else if (departmentId) {
      // department のみある → コース選択（CourseSelect）
      return 'CourseSelect';
    } else {
      // どちらもない → 診療科目選択
      return 'DepartmentSelect';
    }
  }, [departmentId, treatmentKindId]);

  // ステップ管理
  const [step, setStep] = useState<Step>(initialStep);
  useSteps(step, setStep);

  // 各ストアの初期化
  const {
    store,
    transaction: [doRegister, registerStatus],
    vacancyStore,
    session,
  } = useTreatmentRegisterFlow();
  const { waitingListStore, doRegisterWaitingList, registerWaitingListStatus } = useWaitingListRegisterFlow();
  useAutoDepartmentAndCourseSelect(store);

  // キャンペーンコードの自動設定
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const campaign = query.get('CampaignCode');
    if (campaign) {
      store.setCampaignCode(campaign);
    }
  }, [store]);

  // ステップ遷移時の状態に応じたURLのクエリパラメータ設定
  useEffect(() => {
    const url = new URL(window.location.href);
    const baseParams = new URLSearchParamsMonad(location.search)
      .acceptOptional('department', store.department?.id)
      .acceptOptional('treatmentKind', store.treatmentKind?.id);

    const stepActionList = {
      CourseSelect: (params: URLSearchParamsMonad) => params.delete('treatmentKind'),
      RevisitSelect: (params: URLSearchParamsMonad) => params.delete('treatmentKind'),
      VacancySelect: (params: URLSearchParamsMonad) => params,
      DepartmentSelect: (params: URLSearchParamsMonad) => {
        store.treatmentKind = undefined;
        store.department = undefined;
        return params.delete('treatmentKind').delete('department');
      },
    };

    const stepAction = stepActionList[step] || stepActionList.DepartmentSelect;
    const finalParams = stepAction(baseParams);

    url.search = finalParams.toString() ? `?${finalParams.toString()}` : '';

    window.history.replaceState(null, '', url.toString());
  }, [location.search, step, store]);

  // 診療科選択後の処理
  const onSelectDepartment = useCallback(() => {
    if (store.department?.id === 'D' && store.treatmentKind?.id) {
      // コースが選択されている場合は初診として日付選択へ
      store.setRevisit('NEW');
      setStep('VacancySelect');
      return;
    }
    if (store.department?.id === 'D') {
      setStep('RevisitSelect');
      return;
    }
    // 診療科とコースが決まってたら日付選択へ
    if (store.department?.id && store.treatmentKind?.id) {
      // 任意の診療科を選択後ブラウザバック。他の診療科を選択した場合にdepartmentIdとtreatmentKindIdの整合性が取れていないためIDの確認処理を追加。

      if (CourseStore.treatmentKindIds[store.department.id]?.includes(store.treatmentKind?.id)) {
        setStep('VacancySelect');
        return;
      }
    }
    switch (store.department?.type) {
      case DepartmentType.Standard:
      default:
        setStep('VacancySelect');
        return;
      case DepartmentType.CourseBased:
        setStep('CourseSelect');
        return;
      case DepartmentType.Checkup:
        push(Routes.Paths.CheckupReservationNewIndividual);
    }
  }, [push, store]);

  // パンくず定義
  const breadCrumbs = useMemo(() => {
    const base = [
      {
        label: 'TOP',
        step: 'DepartmentSelect',
        onClick: () => {
          push(Routes.Paths.Root);
        },
      },
      {
        label: '診療科目選択',
        step: 'DepartmentSelect',
        onClick: () => {
          setStep('DepartmentSelect');
          store.department = undefined;
          store.treatmentKind = undefined;
        },
      },
    ];

    switch (step) {
      case 'CourseSelect':
        return [
          ...base,
          {
            label: `${store.department?.name}のコースの選択`,
            step: 'CourseSelect',
            onClick: () => {
              setStep('CourseSelect');
            },
          },
        ];

      case 'RevisitSelect':
        return [
          ...base,
          {
            label: '来院・再来院の選択',
            step: 'RevisitSelect',
            onClick: () => {
              setStep('RevisitSelect');
            },
          },
        ];

      case 'VacancySelect':
        return [
          ...base,
          ...(store.department?.type === DepartmentType.CourseBased
            ? [
                {
                  label: `${store.department?.name}のコースの選択`,
                  step: 'CourseSelect',
                  onClick: () => {
                    setStep('CourseSelect');
                  },
                },
              ]
            : []),
          {
            label: '予約日時の選択',
            step: 'VacancySelect',
            onClick: () => {
              setStep('VacancySelect');
            },
          },
        ];

      case 'DepartmentSelect':
      default:
        return base;
    }
  }, [step, push, store]);

  return (
    <>
      <AccountRestrictedToast accountRestricted={!!sessionStore.user?.userEntity?.accountRestricted} />
      <ReservationStore.Context.Provider value={store}>
        <VacanciesStore.Context.Provider value={vacancyStore}>
          <WaitingListStore.Context.Provider value={waitingListStore}>
            <Modal visible={!!registerStatus.running || !!registerWaitingListStatus.running}>
              <Card>予約状況を確認中...</Card>
            </Modal>
            <BreadCrumb items={breadCrumbs} />
            {(() => {
              switch (step) {
                default:
                case 'DepartmentSelect':
                  return <DepartmentSelectPage onNext={onSelectDepartment} />;
                case 'CourseSelect':
                  return <CourseSelectPage onNext={() => setStep('VacancySelect')} />;
                case 'RevisitSelect':
                  return <RevisitSelectPage onNext={() => setStep('CourseSelect')} />;
                case 'VacancySelect':
                  return (
                    <VacancySelectPage
                      onNext={(type?: string) => {
                        doRegister(session, type);
                      }}
                      onRegisterWaitingList={() => {
                        doRegisterWaitingList();
                      }}
                    />
                  );
              }
            })()}
          </WaitingListStore.Context.Provider>
        </VacanciesStore.Context.Provider>
      </ReservationStore.Context.Provider>
    </>
  );
});
